import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Hammer, MapPin, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectsSection = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await axios.get(`${API}/public/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const openGallery = (project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
  };

  const closeGallery = () => {
    setSelectedProject(null);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (selectedProject && selectedProject.images) {
      setCurrentImageIndex((prev) => 
        prev === selectedProject.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedProject && selectedProject.images) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? selectedProject.images.length - 1 : prev - 1
      );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Hammer className="h-4 w-4" />
            Els Nostres Treballs
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Projectes Realitzats
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Descobreix alguns dels treballs que hem realitzat per als nostres clients
          </p>
        </div>

        {projects.length === 0 ? (
          <div className="text-center py-12">
            <Hammer className="h-16 w-16 mx-auto text-amber-300 mb-4" />
            <p className="text-slate-500">Properament mostrarem els nostres treballs realitzats</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => (
            <Card 
              key={project._id || project.id} 
              className="border-0 shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer group"
              onClick={() => openGallery(project)}
            >
              <div className="relative h-56 bg-slate-100 overflow-hidden">
                {project.images && project.images.length > 0 ? (
                  <img
                    src={project.images[0]}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <Hammer className="h-16 w-16 text-slate-300" />
                  </div>
                )}
                {project.images && project.images.length > 1 && (
                  <span className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded">
                    {project.images.length} fotos
                  </span>
                )}
                <span className="absolute top-3 left-3 bg-emerald-600 text-white text-xs px-3 py-1.5 rounded-full shadow-lg">
                  {project.service_type}
                </span>
              </div>
              <CardContent className="p-5">
                <h3 className="text-xl font-bold text-slate-900 mb-2">{project.title}</h3>
                <p className="text-slate-600 text-sm line-clamp-2 mb-4">{project.description}</p>
                <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                  {project.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {project.location}
                    </span>
                  )}
                  {project.date && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {project.date}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        )}
      </div>

      {/* Gallery Modal */}
      <Dialog open={!!selectedProject} onOpenChange={closeGallery}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="text-xl">{selectedProject?.title}</DialogTitle>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
                {selectedProject?.service_type}
              </span>
              {selectedProject?.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {selectedProject.location}
                </span>
              )}
              {selectedProject?.date && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {selectedProject.date}
                </span>
              )}
            </div>
          </DialogHeader>
          
          {selectedProject && selectedProject.images && selectedProject.images.length > 0 && (
            <div className="relative">
              <div className="aspect-[16/10] bg-slate-900">
                <img
                  src={selectedProject.images[currentImageIndex]}
                  alt={`${selectedProject.title} - Foto ${currentImageIndex + 1}`}
                  className="w-full h-full object-contain"
                />
              </div>
              
              {selectedProject.images.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full"
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full"
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  >
                    <ChevronRight className="h-6 w-6" />
                  </Button>
                  
                  {/* Thumbnail navigation */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {selectedProject.images.map((_, index) => (
                      <button
                        key={index}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                        }`}
                        onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(index); }}
                      />
                    ))}
                  </div>
                </>
              )}
              
              {/* Image counter */}
              <div className="absolute top-4 right-4 bg-black/60 text-white text-sm px-3 py-1 rounded-full">
                {currentImageIndex + 1} / {selectedProject.images.length}
              </div>
            </div>
          )}
          
          <div className="p-4">
            <p className="text-slate-700">{selectedProject?.description}</p>
          </div>
          
          {/* Thumbnail strip */}
          {selectedProject && selectedProject.images && selectedProject.images.length > 1 && (
            <div className="px-4 pb-4">
              <div className="flex gap-2 overflow-x-auto py-2">
                {selectedProject.images.map((img, index) => (
                  <button
                    key={index}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                      index === currentImageIndex ? 'border-emerald-500' : 'border-transparent'
                    }`}
                    onClick={() => setCurrentImageIndex(index)}
                  >
                    <img
                      src={img}
                      alt={`Miniatura ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ProjectsSection;
